/**
 * @vitest-environment jsdom
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import {
  distributionsInMonth,
  PAY_PERIOD_CADENCES,
  type PayPeriodCadence,
} from './engine/cashPolicy'
import { monthKeyFromT } from './engine/calendar'
import type { DriverParams } from './engine/types'

export type PayPeriodParams = {
  cadence: PayPeriodCadence
  anchor_date: string
}

type PayDatesPopoverProps = {
  lineLabel: string
  lastClosedMonth: string
  initialParams?: PayPeriodParams
  anchorRef: React.RefObject<HTMLElement | null>
  onCancel: () => void
  onConfirm: (params: PayPeriodParams) => void
}

const CADENCE_OPTIONS: SelectOption[] = PAY_PERIOD_CADENCES.map((cadence) => ({
  id: cadence,
  label: cadence,
}))

function threePayMonths(
  cadence: PayPeriodCadence,
  anchorDate: string,
  lastClosedMonth: string,
): string[] {
  const months: string[] = []
  for (let t = 1; t <= 36; t += 1) {
    const monthKey = monthKeyFromT(lastClosedMonth, t)
    if (distributionsInMonth(cadence, anchorDate, monthKey) === 3) {
      months.push(monthKey)
    }
  }
  return months
}

export default function PayDatesPopover({
  lineLabel,
  lastClosedMonth,
  initialParams,
  anchorRef,
  onCancel,
  onConfirm,
}: PayDatesPopoverProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const [cadence, setCadence] = useState<PayPeriodCadence>(initialParams?.cadence ?? 'biweekly')
  const [anchorDate, setAnchorDate] = useState(initialParams?.anchor_date ?? '')
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const parsedParams = useMemo((): PayPeriodParams | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(anchorDate)) {
      return null
    }
    return { cadence, anchor_date: anchorDate }
  }, [anchorDate, cadence])

  const threePay = useMemo(() => {
    if (!parsedParams) {
      return []
    }
    return threePayMonths(parsedParams.cadence, parsedParams.anchor_date, lastClosedMonth)
  }, [lastClosedMonth, parsedParams])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) {
      return
    }
    const anchor = anchorRef.current
    const panelRect = panel.getBoundingClientRect()
    const padding = 8
    if (!anchor) {
      setPosition({
        top: Math.max(padding, (window.innerHeight - panelRect.height) / 2),
        left: Math.max(padding, (window.innerWidth - panelRect.width) / 2),
      })
      return
    }
    const anchorRect = anchor.getBoundingClientRect()
    let top = anchorRect.bottom + 4
    let left = anchorRect.left
    if (left + panelRect.width > window.innerWidth - padding) {
      left = Math.max(padding, window.innerWidth - panelRect.width - padding)
    }
    if (top + panelRect.height > window.innerHeight - padding) {
      top = Math.max(padding, anchorRect.top - panelRect.height - 4)
    }
    setPosition({ top, left })
  }, [anchorRef])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90]" onClick={onCancel} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed z-[100] w-72 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
        style={{ top: position.top, left: position.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="text-sm font-semibold text-gray-900">
          Pay dates — {lineLabel}
        </h3>
        <div className="mt-3 space-y-3">
          <label className="block text-xs text-gray-600">
            Cadence
            <SearchableSelect
              options={CADENCE_OPTIONS}
              value={cadence}
              placeholder="Cadence"
              size="compact"
              preserveOrder
              allowClear={false}
              useViewportForPlacement
              className="mt-1 w-full min-w-0"
              onChange={(id) => setCadence((id ?? 'biweekly') as PayPeriodCadence)}
            />
          </label>
          <label className="block text-xs text-gray-600">
            Anchor date
            <input
              type="date"
              className="input mt-1 box-border h-8 w-full px-2 text-sm"
              value={anchorDate}
              onChange={(e) => setAnchorDate(e.target.value)}
            />
          </label>
          <p className="text-[10px] text-gray-600">
            {threePay.length > 0
              ? `3-pay months: ${threePay.join(', ')}`
              : 'No 3-pay months in this horizon.'}
          </p>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!parsedParams}
            onClick={() => parsedParams && onConfirm(parsedParams)}
          >
            OK
          </Button>
        </div>
      </div>
    </>,
    document.body,
  )
}

export function payPeriodParamsFromDriver(params?: DriverParams): PayPeriodParams | undefined {
  if (!params?.cadence || !params.anchor_date) {
    return undefined
  }
  if (!(PAY_PERIOD_CADENCES as readonly string[]).includes(params.cadence)) {
    return undefined
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(params.anchor_date)) {
    return undefined
  }
  return { cadence: params.cadence, anchor_date: params.anchor_date }
}
